import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowRight, CheckCircle2, Package, RotateCcw, Truck, AlertTriangle, Wallet as WalletIcon, IndianRupee, Percent } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge } from "../TrackPage";

const COLORS = ["#FF5A1F", "#1D4ED8", "#059669", "#D97706", "#64748B", "#0A1128", "#EF4444"];

function Kpi({ icon: Icon, label, value, testid, tone = "text-navy" }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
        <Icon className="h-4 w-4 text-saffron" />
      </div>
      <p className={`mt-2 font-heading text-2xl font-extrabold tracking-tight ${tone}`}>{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/shipments?limit=6").then((r) => setRecent(r.data.items)).catch(() => {});
  }, []);

  if (!stats) return <p className="py-20 text-center text-slate-400" data-testid="dashboard-loading">Loading dashboard…</p>;

  return (
    <div data-testid="customer-dashboard" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">Welcome, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-1 text-sm text-slate-500">Your shipping command center</p>
        </div>
        <Link to="/app/book" data-testid="dashboard-book-btn"
              className="flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover">
          Book Shipment <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Package} label="Total Shipments" value={stats.total} testid="kpi-total" />
        <Kpi icon={Truck} label="In Transit" value={stats.in_transit} testid="kpi-transit" />
        <Kpi icon={CheckCircle2} label="Delivered" value={stats.delivered} testid="kpi-delivered" tone="text-emerald-600" />
        <Kpi icon={AlertTriangle} label="NDR / RTO" value={`${stats.ndr} / ${stats.rto}`} testid="kpi-ndr-rto" tone="text-amber-600" />
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div data-testid="wallet-card" className="relative overflow-hidden rounded-2xl bg-navy p-6 text-white grain">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-saffron/30 blur-2xl" />
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Wallet Balance</p>
            <WalletIcon className="h-4 w-4 text-saffron" />
          </div>
          <p data-testid="wallet-balance-amount" className="mt-2 font-heading text-3xl font-extrabold">₹{stats.wallet_balance.toLocaleString("en-IN")}</p>
          <Link to="/app/wallet" data-testid="wallet-recharge-btn"
                className="mt-4 inline-block rounded-full bg-saffron px-4 py-2 text-xs font-bold transition-colors hover:bg-saffron-hover">
            Top Up Wallet
          </Link>
        </div>
        <Kpi icon={IndianRupee} label="Total Spend" value={`₹${stats.spend.toLocaleString("en-IN")}`} testid="kpi-spend" />
        <Kpi icon={IndianRupee} label="COD Collected" value={`₹${stats.cod_collected.toLocaleString("en-IN")}`} testid="kpi-cod" />
        {(user?.role === "franchise" || user?.role === "agent") && (
          <Kpi icon={Percent} label="Commission Earned" value={`₹${stats.commission_total.toLocaleString("en-IN")}`} testid="kpi-commission" tone="text-emerald-600" />
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading text-lg font-bold text-navy">Shipment Volume (14 days)</h3>
          <div className="mt-4 h-56" data-testid="chart-volume">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.by_day}>
                <defs>
                  <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF5A1F" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FF5A1F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={24} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#FF5A1F" strokeWidth={2} fill="url(#vol)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading text-lg font-bold text-navy">Status Distribution</h3>
          <div className="mt-4 h-56" data-testid="chart-status">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.by_status} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {stats.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-navy">Recent Shipments</h3>
          <Link to="/app/shipments" className="text-sm font-bold text-saffron hover:underline">View all</Link>
        </div>
        {recent.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="recent-empty">No shipments yet. Book your first shipment!</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-shipments-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="pb-3 pr-4">AWB</th><th className="pb-3 pr-4">Destination</th>
                  <th className="pb-3 pr-4">Courier</th><th className="pb-3 pr-4">Price</th><th className="pb-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="py-3 pr-4"><Link to={`/app/shipments/${s.id}`} className="font-mono font-semibold text-express hover:underline">{s.awb}</Link></td>
                    <td className="py-3 pr-4">{s.destination_city}</td>
                    <td className="py-3 pr-4">{s.courier_name}</td>
                    <td className="py-3 pr-4 font-semibold">₹{s.customer_price}</td>
                    <td className="py-3"><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
